import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useAppContext, Screen } from '../../contexts/AppContext';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface ScopedListBannerProps {
  /** Label for the "Exit to <module>" button, e.g. "Products", "Contacts". */
  moduleLabel: string;
  /** Icon for the "Exit to <module>" button. */
  moduleIcon: string;
  /** Screen (+ optional param/routeParams) to land on when exiting back to the plain module view. */
  moduleScreen: Screen;
  moduleScreenParam?: string;
  moduleScreenRouteParams?: Record<string, any>;
}

/**
 * "You're viewing a scoped subset of this module through a Data List"
 * indicator — two distinct exits (back to the Data Lists index, or back to
 * the plain unscoped module view), not one, since collapsing them caused an
 * exit -> immediate bounce-back-in loop on Products (DataListRecordsScreen
 * unconditionally redirects any system list straight back into the scoped
 * screen). Originally Products-only (ProductsListScreen.tsx); extracted so
 * Contacts and any future system-module list screen can adopt the same
 * pattern instead of re-implementing it.
 */
export const ScopedListBanner: React.FC<ScopedListBannerProps> = ({
  moduleLabel,
  moduleIcon,
  moduleScreen,
  moduleScreenParam,
  moduleScreenRouteParams,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();

  return (
    <View style={styles.scopedExitRow}>
      <TouchableOpacity
        style={styles.exitButton}
        onPress={() => navigateTo('data-lists')}
        activeOpacity={0.7}
      >
        <Ionicons name="exit-outline" size={15} color={colors.primary as string} />
        <Text style={styles.exitButtonText}>Exit to Data Lists</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.exitButton}
        onPress={() => navigateTo(moduleScreen, moduleScreenParam, moduleScreenRouteParams)}
        activeOpacity={0.7}
      >
        <Ionicons name={moduleIcon as any} size={15} color={colors.primary as string} />
        <Text style={styles.exitButtonText}>Exit to {moduleLabel}</Text>
      </TouchableOpacity>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  scopedExitRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  exitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: withAlpha(colors.primary, 0.06),
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.19),
  },
  exitButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary as string,
    fontFamily: typography.fontFamily.semibold,
  },
});
